import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import TextField from "@mui/material/TextField";

import DropdownSelector from "../../../atoms/Common/DropdownSelector.tsx";
import { LGNumber } from "../../../atoms/Inputs/LGNumber.tsx";
import {
  getCatalogue,
  getTaxRegimeList,
} from "../../../api/common/catalogues.ts";
import {
  getExpenseAccountList,
  getTaxRetentionById,
  updateTaxRetention,
} from "../../../api/accounting";
import { useSnack } from "../../../services/SnakbarProvider";
import { handleError } from "../../../services/errorHandler";

const emptyToNull = (value, originalValue) =>
  originalValue === "" || originalValue === null || originalValue === undefined
    ? null
    : value;

const nullableInt = () =>
  Yup.number().transform(emptyToNull).integer().nullable();

const validationSchema = Yup.object().shape({
  providerTypeId: Yup.number()
    .integer()
    .required("El tipo de proveedor es obligatorio"),
  taxRegimeId: nullableInt(),
  economicActivityId: nullableInt(),
  ivaRate: Yup.number().min(0).required("El IVA es obligatorio"),
  isrRate: Yup.number().min(0).required("El ISR es obligatorio"),
  ivaRetentionAccountId: nullableInt(),
  isrRetentionAccountId: nullableInt(),
  description: Yup.string().nullable(),
});

const toNullableInt = (value) =>
  value === "" || value === undefined || value === null ? null : Number(value);

export default function UpdateTaxRetentionForm({ id, companyId, onSubmit }) {
  const { openSnack } = useSnack();

  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [selectedProviderTypeId, setSelectedProviderTypeId] = useState(null);
  const [taxRegimes, setTaxRegimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExpenseAccounts, setLoadingExpenseAccounts] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(true);

  const [formData, setFormData] = useState({
    companyId: companyId ?? "",
    providerTypeId: "",
    taxRegimeId: "",
    economicActivityId: "",
    ivaRate: 0,
    isrRate: 0,
    ivaRetentionAccountId: "",
    isrRetentionAccountId: "",
    description: "",
  });

  useEffect(() => {
    const fetchExpenseAccounts = async () => {
      if (!companyId) return;
      setLoadingExpenseAccounts(true);
      try {
        const res = await getExpenseAccountList({
          companyId,
          pageNumber: 1,
          pageSize: 1000,
        });
        setExpenseAccounts(res.data);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoadingExpenseAccounts(false);
      }
    };

    fetchExpenseAccounts();
  }, [companyId]);

  useEffect(() => {
    const fetchTaxRegimes = async () => {
      if (selectedProviderTypeId == null) {
        setTaxRegimes([]);
        return;
      }
      setLoading(true);
      try {
        const res = await getTaxRegimeList({
          providerTypeId: selectedProviderTypeId,
        });
        setTaxRegimes(res.data);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchTaxRegimes();
  }, [selectedProviderTypeId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoadingRecord(true);
      try {
        const res = await getTaxRetentionById(id);
        const data = res.data;

        setFormData({
          companyId: data.companyId ?? companyId ?? "",
          providerTypeId: data.providerTypeId ?? "",
          taxRegimeId: data.taxRegimeId ?? "",
          economicActivityId: data.economicActivityId ?? "",
          ivaRate: data.ivaRate ?? 0,
          isrRate: data.isrRate ?? 0,
          ivaRetentionAccountId: data.ivaRetentionAccountId ?? "",
          isrRetentionAccountId: data.isrRetentionAccountId ?? "",
          description: data.description ?? "",
        });

        setSelectedProviderTypeId(data.providerTypeId ?? null);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoadingRecord(false);
      }
    };

    fetchData();
  }, [id, companyId]);

  if (loadingRecord) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = {
              providerTypeId: toNullableInt(values.providerTypeId),
              taxRegimeId: toNullableInt(values.taxRegimeId),
              economicActivityId: toNullableInt(values.economicActivityId),
              ivaRate: Number(values.ivaRate),
              isrRate: Number(values.isrRate),
              ivaRetentionAccountId: toNullableInt(values.ivaRetentionAccountId),
              isrRetentionAccountId: toNullableInt(values.isrRetentionAccountId),
              description: values.description ?? "",
            };

            await updateTaxRetention(id, payload);
            openSnack("Retención fiscal actualizada con éxito", "success");
            onSubmit?.();
          } catch (e) {
            openSnack(handleError(e), "error");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  name="providerTypeId"
                  label="Tipo de proveedor"
                  as={DropdownSelector}
                  fetchFunction={getCatalogue}
                  fetchParams={{ CatalogueName: "PROVIDER_TYPE" }}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  onChange={(event, value) => {
                    const nextProviderTypeId = value?.id ?? null;
                    setSelectedProviderTypeId(nextProviderTypeId);
                    setFieldValue("taxRegimeId", "");
                  }}
                />
              </Grid>

              {loading ? (
                <Grid item xs={12}>
                  <CircularProgress size={24} />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Field
                      name="taxRegimeId"
                      label="Régimen fiscal"
                      as={DropdownSelector}
                      parentOptions={taxRegimes}
                      optionLabel={["code", "name"]}
                      optionSeparator=" | "
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="economicActivityId"
                      label="Actividad económica"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>
                </>
              )}

              {loadingExpenseAccounts ? (
                <Grid item xs={12}>
                  <CircularProgress size={24} />
                </Grid>
              ) : (
                <>
                  <Grid item xs={3}>
                    <Field
                      name="ivaRate"
                      label="Tasa IVA"
                      as={LGNumber}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <Field
                      name="ivaRetentionAccountId"
                      label="Cuenta retención IVA"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Field
                      name="isrRate"
                      label="Tasa ISR"
                      as={LGNumber}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <Field
                      name="isrRetentionAccountId"
                      label="Cuenta retención ISR"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Field
                  fullWidth
                  name="description"
                  label="Descripción"
                  as={TextField}
                  multiline
                  minRows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                >
                  Actualizar
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
